import { useEffect, useRef, useState } from 'react';
import { posApi } from '../../../api/pos';
import { unwrapResponse } from '../../../api/client';

export interface SelectedSalesPerson {
  id: number;
  name: string;
}

interface SalesPersonPanelProps {
  selectedSalesPerson: SelectedSalesPerson | null;
  onSelectSalesPerson: (person: SelectedSalesPerson | null) => void;
  clearTrigger?: number;
}

export default function SalesPersonPanel({
  selectedSalesPerson,
  onSelectSalesPerson,
  clearTrigger,
}: SalesPersonPanelProps) {
  const [salesPersonList, setSalesPersonList] = useState<SelectedSalesPerson[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const outletRaw = localStorage.getItem('outlet');
        const outletId = outletRaw ? Number(JSON.parse(outletRaw)?.id) : null;
        if (!outletId) return;
        const res = await posApi.getActiveOutletSalesPersons(outletId);
        const data = unwrapResponse<any[]>(res);
        if (Array.isArray(data)) {
          setSalesPersonList(
            data.map((p: any) => ({
              id: Number(p?.salePersonId ?? p?.id),
              name: p?.salePersonName ?? p?.name ?? '',
            }))
          );
        }
      } catch {
        setError('Failed to load sales persons.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (clearTrigger && clearTrigger > 0) {
      onSelectSalesPerson(null);
    }
  }, [clearTrigger]);

  const label = selectedSalesPerson ? selectedSalesPerson.name : 'Select Sales Person';

  return (
    <div className="p-4 border-b border-gray-200">
      <label className="block text-sm font-bold text-gray-900 mb-2 whitespace-nowrap">
        Sales Person <span className="text-red-600">*</span>
      </label>
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsDropdownOpen((prev) => !prev)}
          className="w-full px-3 py-2.5 border-2 border-sky-500 rounded-lg text-left text-sm bg-white flex items-center justify-between"
        >
          <span className={selectedSalesPerson ? 'text-slate-900' : 'text-slate-400'}>
            {label}
          </span>
          <i className={`ri-arrow-${isDropdownOpen ? 'up' : 'down'}-s-line text-slate-500`} />
        </button>

        {isDropdownOpen && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-sky-300 rounded-lg shadow-lg overflow-hidden">
            <div className="max-h-52 overflow-auto">
              {isLoading && (
                <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
              )}
              {!isLoading && salesPersonList.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-500">No sales person found</div>
              )}
              {!isLoading &&
                salesPersonList.map((person) => (
                  <button
                    type="button"
                    key={person.id}
                    onClick={() => {
                      onSelectSalesPerson(person);
                      setIsDropdownOpen(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-100 ${
                      selectedSalesPerson?.id === person.id ? 'bg-slate-200' : ''
                    }`}
                  >
                    {person.name}
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </div>
  );
}
